// Primitivas de diálogo modal — sistema de diseño "Audiophile Precision".
//
// Antes no existía una primitiva de diálogo: las confirmaciones críticas (p. ej.
// borrado de cuenta) se construían inline por pantalla, con estilos repetidos y
// sin foco atrapado. `Modal` centraliza el comportamiento accesible:
//  - `role="dialog"` + `aria-modal="true"` con título asociado (`aria-labelledby`).
//  - Portal a `document.body` (no hereda overflow/transform de los padres).
//  - FOCO ATRAPADO dentro del diálogo; Tab/Shift+Tab circulan.
//  - Cierre con `Escape` y clic en el velo.
//  - Devuelve el foco al elemento que lo abrió al cerrarse.
//  - Bloquea el scroll del fondo mientras está abierto.
//
// `ConfirmDialog` es el caso de uso más común (confirmar/cancelar), con un tono
// `danger` para acciones destructivas.

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './components.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Acciones del pie (botones). */
  footer?: ReactNode;
  /** Icono opcional junto al título. */
  icon?: string;
  /** Si `false`, el clic en el velo NO cierra (para flujos que exigen decisión). */
  dismissOnBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  icon,
  dismissOnBackdrop = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`);

  // Guarda el foco previo y bloquea el scroll del fondo al abrir; restaura al cerrar.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Enfoca el primer elemento enfocable del diálogo (o el contenedor).
    const node = dialogRef.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      // Devuelve el foco al disparador (si sigue en el DOM).
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Escape cierra; Tab atrapa el foco dentro del diálogo.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      const activeEl = document.activeElement as HTMLElement;
      if (e.shiftKey && activeEl === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && activeEl === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    },
    [onClose],
  );

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-margin-mobile bg-surface-base/70 backdrop-blur-sm"
      onMouseDown={(e) => {
        // Solo cierra si el clic empezó en el velo (no al arrastrar desde dentro).
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="w-full max-w-md max-h-[85vh] overflow-auto rounded-2xl bg-surface-deck border border-border-subtle shadow-2xl outline-none flex flex-col"
      >
        <div className="flex items-start justify-between gap-space-sm p-space-md border-b border-border-subtle">
          <h2
            id={titleId.current}
            className="flex items-center gap-space-xs font-headline-sm text-headline-sm text-on-surface uppercase tracking-wider"
          >
            {icon && <Icon name={icon} className="text-primary text-[20px]" />}
            {title}
          </h2>
          <ModalCloseButton onClose={onClose} />
        </div>
        <div className="p-space-md flex-1">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-space-sm p-space-md border-t border-border-subtle">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ModalCloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('common.close')}
      className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-on-surface active:scale-95 transition-transform"
    >
      <Icon name="close" className="text-[20px]" />
    </button>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: ReactNode;
  /** Rótulo del botón de confirmación (por defecto `common.confirm`). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Tono de la acción: `danger` para irreversibles (rojo). */
  tone?: 'default' | 'danger';
  /** Deshabilita el botón de confirmar (p. ej. mientras procesa). */
  busy?: boolean;
  icon?: string;
}

/** Diálogo de confirmación estándar (confirmar / cancelar). */
export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  busy = false,
  icon,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      icon={icon ?? (tone === 'danger' ? 'warning' : undefined)}
      dismissOnBackdrop={!busy}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn-danger' : 'btn-cyan'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </>
      }
    >
      <div className="font-body-md text-body-md text-on-surface-variant">{message}</div>
    </Modal>
  );
}
